/**
 * Centralized API Utility
 * 
 * This module provides a secure way to make API calls with automatic
 * credential handling using HttpOnly cookies.
 * 
 * SECURITY: JWTs are stored in HttpOnly cookies (not localStorage)
 * to prevent XSS attacks from stealing authentication tokens.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

interface ApiOptions extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Make an authenticated API request
 * Automatically includes credentials (cookies) with every request
 */
export async function apiRequest(endpoint: string, options: ApiOptions = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers };

  const fetchOptions: RequestInit = {
    credentials: 'include' as RequestCredentials,
    ...options,
    headers: defaultHeaders };
  
  const response = await fetch(url, fetchOptions);
  return response;
}

/**
 * GET request
 */
export async function apiGet(endpoint: string, options: ApiOptions = {}) {
  return apiRequest(endpoint, { ...options, method: 'GET' });
}

/**
 * POST request
 */
export async function apiPost(endpoint: string, data: any, options: ApiOptions = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data) });
}

/**
 * PUT request
 */
export async function apiPut(endpoint: string, data: any, options: ApiOptions = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data) });
}

/**
 * PATCH request
 */
export async function apiPatch(endpoint: string, data: any, options: ApiOptions = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data) });
}

/**
 * DELETE request
 */
export async function apiDelete(endpoint: string, options: ApiOptions = {}) {
  return apiRequest(endpoint, { ...options, method: 'DELETE' });
}

/**
 * POST request with FormData (for file uploads)
 */
export async function apiPostFormData(endpoint: string, formData: FormData, options: ApiOptions = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: formData as any,
    headers: {
      // Don't set Content-Type for FormData - browser will set it with boundary
      ...options.headers } });
}

/**
 * Login user
 * Sets HttpOnly cookie automatically via server response
 */
export async function login(email: string, password: string) {
  const response = await apiPost('/api/auth/login', { email, password });
  
  if (response.ok) {
    const data = await response.json();
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  } else {
    const error = await response.json();
    throw new Error(error.detail || 'Login failed');
  }
}

/**
 * Logout user
 * Clears HttpOnly cookie via server
 */
export async function logout() {
  try {
    await apiPost('/api/auth/logout', {});
  } catch (e: any) {
    console.warn('Logout API call failed:', e);
  }
  
  localStorage.removeItem('user');
}

/**
 * Register new user
 * Sets HttpOnly cookie automatically via server response
 */
export async function register(userData: any) {
  const response = await apiPost('/api/auth/register', userData);
  
  if (response.ok) {
    const data = await response.json();
    // For backward compatibility
    if (data.access_token) {
    }
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  } else {
    const error = await response.json();
    throw new Error(error.detail || 'Registration failed');
  }
}

/**
 * Get current user from server
 */
export async function getCurrentUser() {
  const response = await apiGet('/api/auth/me');
  
  if (response.ok) {
    return response.json();
  } else if (response.status === 401) {
    localStorage.removeItem('user');
    return null;
  } else {
    throw new Error('Failed to get current user');
  }
}

/**
 * Check if user is authenticated
 * Tries to get current user from server
 */
export async function isAuthenticated() {
  try {
    const user = await getCurrentUser();
    return !!user;
  } catch {
    return false;
  }
}

export default {
  apiRequest,
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiPostFormData,
  login,
  logout,
  register,
  getCurrentUser,
  isAuthenticated,
};
